import React, { useState } from "react";
import { Phone, Smartphone, Volume2, Settings2 } from "lucide-react";
import { VoicemailOutlined, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { getActionDetailsLabel } from "../utils/actionDetailsLabel";
import { formatPhoneNumber } from "../utils/formatnumber";
import { formatTimestampForZone } from "../utils/formattimestamp";

export const ElevenLabsIcon = ({ className = "w-4 h-4" }) => {
  const [imgError, setImgError] = useState(false);

  if (imgError) {
    return (
      <div
        className={`${className} bg-indigo-500 rounded flex items-center justify-center text-white text-xs font-bold`}
      >
        11
      </div>
    );
  }

  return (
    <img
      src="/icons/elevenlabs.svg"
      alt="Eleven Labs"
      className={className}
      onError={() => setImgError(true)}
    />
  );
};

export const formatActionLabel = (action) => {
  if (!action || action === "N/A") return action;

  switch (action) {
    case "play_recording":
      return "Play Recording";
    case "forward":
      return "Forwarded";
    case "forwardvwm":
      return "Forwarded w/VM";
    case "twilanswer_app":
      return "Twilanswer App";
    case "dynamic_forward":
      return "Dynamic Forward";
    case "send_to_vm":
      return "Sent to VM";
    case "ai_agent":
      return "AI Agent";
    case "ivr":
      return "Call Menu";
    case "Outbound Call":
      return "Outbound Call";
    default:
      return action;
  }
};

export const VoicemailDroppedPill = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 whitespace-nowrap">
    <VoicemailOutlined sx={{ fontSize: "14px" }} />
    Voicemail dropped
  </span>
);

export const renderActionCell = (action, { voicemailDropped = false } = {}) => {
  if (!action || action === "N/A") return null;

  switch (action) {
    case "play_recording":
      return (
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-yellow-500 shrink-0" />
          <span className="text-sm text-gray-900">Play Recording</span>
        </div>
      );
    case "forward":
      return (
        <div className="flex items-center gap-2">
          <ArrowForwardIcon sx={{ fontSize: "16px", color: "#10B981" }} />
          <span className="text-sm text-gray-900">Forwarded</span>
        </div>
      );
    case "forwardvwm":
      return (
        <div className="flex items-center gap-2">
          <ArrowForwardIcon sx={{ fontSize: "16px", color: "#10B981" }} />
          <span className="text-sm text-gray-900">Forwarded w/VM</span>
        </div>
      );
    case "twilanswer_app":
      return (
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-green-500 shrink-0" />
          <span className="text-sm text-gray-900">Twilanswer App</span>
        </div>
      );
    case "dynamic_forward":
      return (
        <div className="flex items-center gap-2">
          <ArrowForwardIcon sx={{ fontSize: "16px", color: "#10B981" }} />
          <span className="text-sm text-gray-900">Dynamic Forward</span>
        </div>
      );
    case "send_to_vm":
      return (
        <div className="flex items-center gap-2">
          <VoicemailOutlined sx={{ fontSize: "16px", color: "#6366F1" }} />
          <span className="text-sm text-gray-900">Sent to VM</span>
        </div>
      );
    case "ai_agent":
      return (
        <div className="flex items-center gap-2">
          <ElevenLabsIcon className="w-4 h-4" />
          <span className="text-sm text-gray-900">AI Agent</span>
        </div>
      );
    case "ivr":
      return (
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-indigo-500 shrink-0" />
          <span className="text-sm text-gray-900">Call Menu</span>
        </div>
      );
    case "Outbound Call":
      return (
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-green-500 shrink-0" />
          <span className="text-sm text-gray-900">Outbound Call</span>
          {voicemailDropped && <VoicemailDroppedPill />}
        </div>
      );
    default:
      return <span className="text-sm text-gray-900">{action}</span>;
  }
};

export const renderActionFilterItem = (action) => {
  if (!action || action === "N/A") {
    return <span>{action}</span>;
  }
  return renderActionCell(action);
};

export const YesNoPill = ({ value }) => {
  const isYes = value === "Yes";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isYes
          ? "bg-emerald-50 text-emerald-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      {value || "No"}
    </span>
  );
};

export const formatCallTime = (callTime) => {
  if (!callTime || callTime === "N/A") return "N/A";
  return formatTimestampForZone(callTime);
};

export const formatPhone = (value) => {
  if (!value || value === "N/A") return "N/A";
  return formatPhoneNumber(value);
};

export { getActionDetailsLabel };
